import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaUpload, FaCheckCircle, FaSpinner, FaFileMedicalAlt, FaDownload, FaTimesCircle, FaCamera } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';
import ConfirmationModal from './ConfirmationModal';
import { useServiceCatalog } from './ServiceCatalogContext';
import OrderStatusTracker from './OrderStatusTracker';

const AccountPageContainer = styled.div`
  max-width: 800px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const Title = styled.h1`
  margin-bottom: 2rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1rem;
`;

const Detail = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1rem;
`;

const LogoutButton = styled.button`
  padding: 10px 20px;
  background-color: #d9534f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-top: 2rem;
`;

const EditButton = styled.button`
  padding: 8px 16px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
`;

const UploadSection = styled.div`
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #eee;
`;

const UploadActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1.5rem;
`;

const UploadList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 1rem;
`;

const UploadItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f8f9fa;
    padding: 0.75rem 1rem;
    border-radius: 6px;
`;

const UploadDetails = styled.div`
  flex-grow: 1;
`;

const UploadLabel = styled.span`
    font-weight: 500;
`;

const UploadButton = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #005eb8;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background-color 0.2s;

    ${props => props.disabled && `
        background-color: #a9a9a9;
        cursor: not-allowed;
        pointer-events: none;
    `}

    &:hover {
        ${props => !props.disabled && `
            background-color: #004a94;
        `}
    }

    input[type="file"] {
        display: none;
    }
`;

const FileInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    overflow: hidden; /* To handle long filenames */

    span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    svg {
        flex-shrink: 0;
    }
`;

const UploadProgressWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    min-width: 180px;
`;

const ProgressBarContainer = styled.div`
    width: 100%;
    height: 6px;
    background-color: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;
`;

const ProgressBarFill = styled.div`
    height: 100%;
    background-color: #005eb8;
    width: ${props => props.width}%;
    transition: width 0.1s ease;
`;

const UploadStatus = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-size: 0.9rem;
    color: #28a745; /* green for success */

    svg {
        font-size: 1.1rem;
    }
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: #d9534f;
    cursor: pointer;
    text-decoration: underline;
    font-size: 0.85rem;
    padding: 0;
    margin-left: 1rem;

    &:hover {
        color: #c9302c;
    }
`;

const NoDocsCheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #555;

    input {
        cursor: pointer;
        accent-color: #005eb8;
    }
    label {
        cursor: pointer;
    }
`;

const SickNoteDatesContainer = styled.div`
    margin-top: 1.5rem;
    padding: 1.25rem;
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const DateInputGroup = styled.div`
    display: flex;
    gap: 1.5rem;
    align-items: flex-end;
    flex-wrap: wrap;
    
    div {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    label {
        font-size: 0.85rem;
        font-weight: 700;
        color: #444;
    }
    
    input {
        padding: 8px 12px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 0.95rem;
    }
`;

const SpinningIcon = styled(FaSpinner)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CancelledStatus = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1.5rem;
    padding: 1rem;
    background-color: #f8d7da;
    color: #721c24;
    border-radius: 6px;
    font-weight: 500;

    svg {
        font-size: 1.2rem;
    }
`;

const RejectedStatus = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.5rem;
    padding: 1rem 1.5rem;
    background-color: #fff3cd; /* A light yellow for warning */
    color: #856404; /* A dark yellow/brown for text */
    border: 1px solid #ffeeba;
    border-radius: 6px;
    font-weight: 500;

    p {
        margin: 0.25rem 0;
    }
`;

const SubmittedStatus = styled.div`
    display: flex;
    align-items: flex-start; /* Align icon to the top for multi-line text */
    gap: 0.75rem;
    margin-top: 1.5rem;
    padding: 1rem;
    background-color: #e8f5e9; /* A light green */
    color: #2e7d32; /* A darker green */
    border-radius: 6px;
    font-weight: 500;

    svg {
        font-size: 1.2rem;
        margin-top: 0.15rem; /* Small adjustment for better alignment */
    }

    span {
        line-height: 1.5;
    }
`;

const CompletedSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-top: 1.5rem;
    padding: 1.5rem;
    background-color: #e8f5e9;
    color: #2e7d32;
    border-radius: 6px;
    text-align: center;

    svg {
        font-size: 2rem;
        margin-bottom: 0.5rem;
    }

    span {
        font-weight: 500;
        font-size: 1.1rem;
        line-height: 1.5;
    }
`;

const DownloadButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.5rem;
    background-color: #28a745;
    color: white;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: bold;
    text-decoration: none;
    margin-top: 1rem;
    transition: background-color 0.2s;

    &:hover {
        background-color: #218838;
    }

    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
`;

const SubmitDocumentsButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px 20px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-top: 1.5rem;
  font-size: 1rem;
  transition: background-color 0.2s;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background-color: #218838;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const CancelReviewButton = styled.button`
  background: none;
  border: 1px solid #f0ad4e;
  color: #f0ad4e;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-top: 1rem;
  font-size: 0.9rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f0ad4e;
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelOrderButton = styled.button`
  display: block;
  width: 100%;
  padding: 10px 20px;
  background-color: transparent;
  color: #d9534f;
  border: 1px solid #d9534f;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-top: 1rem;
  font-size: 0.9rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #d9534f;
    color: white;
  }
`;

const SectionTitle = styled.h2`
    font-size: 1.8rem;
    margin-top: 3rem;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e0e0e0;
`;

const OrderHistoryContainer = styled.div`
    margin-top: 2rem;
`;

const OrderCard = styled.div`
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
`;

const OrderHeader = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #eee;
`;

const OrderInfo = styled.div`
    font-size: 0.9rem;
    color: #555;
    strong {
        color: #333;
    }
`;

const OrderItemsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const OrderItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: flex-start; /* Align items to top for multi-line content */
    padding: 1rem 0;
    font-size: 1rem;
`;

const ItemName = styled.span`
    color: #333;
`;

const ItemDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
  margin-bottom: 0;
  line-height: 1.5;
  max-width: 500px; /* Prevent it from getting too wide on large screens */
`;

const RecordingModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const RecordingModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  position: relative;
  max-width: 680px;
  width: 90%;
  text-align: center;
`;

const CloseModalButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #333;
`;

const VideoContainer = styled.div`
  position: relative;
  margin: 1.5rem 0;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 4 / 3;

  video {
    width: 100%;
    height: 100%;
    display: block;
  }

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
  }
`;

const InstructionsOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 10px;
  font-size: 0.9rem;
`;

const RecordingControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;

  button {
    padding: 10px 20px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    font-weight: bold;
  }

  .start-button { background-color: #005eb8; color: white; }
  .stop-button { background-color: #d9534f; color: white; }
  .retake-button { background-color: #f0ad4e; color: white; }
  .upload-button { background-color: #28a745; color: white; }
  .cancel-button { background-color: #ccc; }
`;

const Address = ({ address, type }) => {
  if (!address) {
    return <Detail><strong>{type} Address:</strong> Not provided</Detail>;
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <strong style={{ fontSize: '1.1rem' }}>{type} Address:</strong>
      <div style={{ marginLeft: '0', marginTop: '0.5rem', lineHeight: '1.5', color: '#555', fontSize: '1.1rem' }}>
        {address.line1 && <div>{address.line1}</div>}
        {address.line2 && <div>{address.line2}</div>}
        {address.city && <div>{address.city}</div>}
        {address.state && <div>{address.state}</div>}
        {address.postal_code && <div>{address.postal_code}</div>}
        {address.country && <div>{address.country}</div>}
      </div>
    </div>
  );
};

// Static data and helper function, defined once outside the component
const productDescriptions = {
  "Self-Certification Sick Note": "Please provide us with a form of ID (driving licence/passport), proof of your illness (recent hospital admission letter, COVID test, appointment letter), and a short video confirming your name, date of birth, why you need the note and the dates you need the note from and too."
};

const getProductDescription = (productName) => {
  return productDescriptions[productName] || null;
};

const Account = () => {
  const { isAuthenticated, user, logout, loading, fetchOrders, uploadFileForOrder, submitDocumentsForOrder, removeFileForOrder, cancelOrderReview, getLetterDownloadUrl } = useAuth();
  const { allServices } = useServiceCatalog();
  const navigate = useNavigate(); // Keep navigate for redirects
  const [orders, setOrders] = useState(user?.orders || []); // Initialize from context
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // Track progress for current upload
  const [noDocuments, setNoDocuments] = useState({});
  const [sickNoteDates, setSickNoteDates] = useState({});

  const [cancelConfirmation, setCancelConfirmation] = useState({ isOpen: false, orderId: null });
  const [loadingStates, setLoadingStates] = useState({
    uploading: null,
    submitting: null,
    cancelling: null,
    downloading: null,
  });

  const [videoRecordingState, setVideoRecordingState] = useState({
    isRecordingModalOpen: false,
    isRecording: false,
    recordedVideoBlob: null,
    isStreamReady: false,
  });

  const [photoCaptureState, setPhotoCaptureState] = useState({
    isOpen: false,
    capturedBlob: null,
    isStreamReady: false,
  });

  const videoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const currentRecordingOrderId = useRef(null);
  const currentPhotoUploadType = useRef(null);

  const refreshOrders = useCallback(async () => {
    setOrdersLoading(true); // Show loading indicator on manual refresh
    const result = await fetchOrders();
    if (result.success) {
      setOrders(result.orders.sort((a, b) => new Date(b.date) - new Date(a.date)));
    }
    setOrdersLoading(false);
    return result;
  }, [fetchOrders]);

  const openRecordingModal = async (orderId) => {
    currentRecordingOrderId.current = orderId;
    setVideoRecordingState(prev => ({ ...prev, isRecordingModalOpen: true, isStreamReady: false }));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 20 } },
        audio: true
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(e => e.name !== 'AbortError' && console.error(e));
      }
      setVideoRecordingState(prev => ({ ...prev, recordedVideoBlob: null, isStreamReady: true }));
    } catch (err) {
      toast.error("Could not access camera.");
      closeRecordingModal();
    }
  };

  const closeRecordingModal = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    }
    mediaStreamRef.current = null;
    setVideoRecordingState({ isRecordingModalOpen: false, isRecording: false, recordedVideoBlob: null, isStreamReady: false });
  };

  const openPhotoModal = async (orderId, type) => {
    currentRecordingOrderId.current = orderId;
    currentPhotoUploadType.current = type;
    setPhotoCaptureState(prev => ({ ...prev, isOpen: true, isStreamReady: false, capturedBlob: null }));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(e => e.name !== 'AbortError' && console.error(e));
      }
      setPhotoCaptureState(prev => ({ ...prev, isStreamReady: true }));
    } catch (err) {
      toast.error("Could not access camera.");
      closePhotoModal();
    }
  };

  const closePhotoModal = () => {
    if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(t => t.stop());
    mediaStreamRef.current = null;
    setPhotoCaptureState({ isOpen: false, capturedBlob: null, isStreamReady: false });
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        setPhotoCaptureState(prev => ({ ...prev, capturedBlob: blob }));
      }, 'image/jpeg', 0.95);
    }
  };

  const startRecording = () => {
    recordedChunksRef.current = [];
    // Define preferred and fallback MIME types
    const mimeTypes = ['video/mp4; codecs=avc1', 'video/webm; codecs=vp9', 'video/webm'];
    let supportedMimeType = '';

    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        supportedMimeType = type;
        break;
      }
    }

    const mediaRecorder = new MediaRecorder(mediaStreamRef.current, { mimeType: supportedMimeType, videoBitsPerSecond: 1000000 });
    mediaRecorder.ondataavailable = (e) => e.data.size > 0 && recordedChunksRef.current.push(e.data);
    mediaRecorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: supportedMimeType || 'video/webm' });
      setVideoRecordingState(prev => ({ ...prev, recordedVideoBlob: blob }));
      if (previewVideoRef.current) previewVideoRef.current.src = URL.createObjectURL(blob);
    };
    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setVideoRecordingState(prev => ({ ...prev, isRecording: true }));
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setVideoRecordingState(prev => ({ ...prev, isRecording: false }));

    // Stop the camera stream immediately to turn off hardware indicators
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
      mediaStreamRef.current = null;
    }
  };

  const retakeVideo = async () => {
    setVideoRecordingState(prev => ({ ...prev, recordedVideoBlob: null, isStreamReady: false }));
    // Re-initialize camera access for the retake
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 20 } },
        audio: true
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(e => e.name !== 'AbortError' && console.error(e));
      }
      setVideoRecordingState(prev => ({ ...prev, isStreamReady: true }));
    } catch (err) {
      toast.error("Could not access camera.");
      closeRecordingModal();
    }
  };

  const retakePhoto = () => {
    setPhotoCaptureState(prev => ({ ...prev, capturedBlob: null }));
    if (videoRef.current) videoRef.current.play();
  };

  const handleUploadCapturedPhoto = async () => {
    if (!photoCaptureState.capturedBlob || !currentRecordingOrderId.current || !currentPhotoUploadType.current) return;
    const type = currentPhotoUploadType.current;
    const orderId = currentRecordingOrderId.current;

    setLoadingStates(prev => ({ ...prev, uploading: { orderId, type } }));
    try {
      const file = new File([photoCaptureState.capturedBlob], `${type}_capture.jpg`, { type: 'image/jpeg' });
      const result = await uploadFileForOrder(orderId, type, file, setUploadProgress);
      if (result.success) {
        await refreshOrders();
        closePhotoModal();
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, uploading: null }));
      setUploadProgress(0);
    }
  };

  const handleFileRemove = async (orderId, uploadType) => {
    if (!window.confirm('Remove this file?')) return;
    setLoadingStates(prev => ({ ...prev, uploading: { orderId, type: uploadType } }));
    try {
      const result = await removeFileForOrder(orderId, uploadType);
      if (result.success) await refreshOrders();
    } finally {
      setLoadingStates(prev => ({ ...prev, uploading: null }));
    }
  };

  const handleSickNoteDateChange = (orderId, field, value) => {
    setSickNoteDates(prev => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || { start: '', end: '', notApplicable: false }),
        [field]: value
      }
    }));
  };

  const handleSubmitDocuments = async (orderId) => {
    if (!window.confirm('Submit documents?')) return;
    setLoadingStates(prev => ({ ...prev, submitting: orderId }));
    try {
      const result = await submitDocumentsForOrder(orderId, { sickNoteDates: sickNoteDates[orderId] });
      if (result.success) await refreshOrders();
    } finally {
      setLoadingStates(prev => ({ ...prev, submitting: null }));
    }
  };

  const handleCancelReview = async (orderId) => {
    if (!window.confirm('Cancel review?')) return;
    setLoadingStates(prev => ({ ...prev, cancelling: orderId }));
    try {
      const result = await cancelOrderReview(orderId);
      if (result.success) await refreshOrders();
    } finally {
      setLoadingStates(prev => ({ ...prev, cancelling: null }));
    }
  };

  const handleNoDocumentsChange = (orderId, type, isChecked) => {
    setNoDocuments(prev => ({
      ...prev,
      [orderId]: { ...(prev[orderId] || {}), [type]: isChecked }
    }));
  };

  const handleFileUpload = async (event, orderId, uploadType) => {
    const file = event.target.files[0];
    if (!file || !orderId) return;

    // Pre-upload check: Ensure file is under 500MB
    const MAX_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error('The selected file is too large. Maximum allowed size is 500MB.');
      return;
    }

    setUploadProgress(0);
    setLoadingStates(prev => ({ ...prev, uploading: { orderId, type: uploadType } }));

    try {
      const result = await uploadFileForOrder(orderId, uploadType, file, (progress) => {
        setUploadProgress(progress);
      });
      if (result.success) {
        toast.success('File uploaded successfully!');
        await refreshOrders(); // Refresh orders to show the new upload status
      } else {
        toast.error(`Upload failed: ${result.error}`);
      }
    } catch (error) {
      toast.error(`An error occurred during upload: ${error.message}`);
    } finally {
      setLoadingStates(prev => ({ ...prev, uploading: null }));
      setUploadProgress(0);
      if (event.target) event.target.value = null; // Reset file input safely
    }
  };

  const handleUploadRecordedVideo = async () => {
    if (!videoRecordingState.recordedVideoBlob || !currentRecordingOrderId.current) return;
    setLoadingStates(prev => ({ ...prev, uploading: { orderId: currentRecordingOrderId.current, type: 'video' } }));
    // Ensure the uploaded file has the correct extension based on its MIME type
    const fileExtension = videoRecordingState.recordedVideoBlob.type.includes('mp4') ? '.mp4' : '.webm';
    const file = new File([videoRecordingState.recordedVideoBlob], `video-recording${fileExtension}`, { type: videoRecordingState.recordedVideoBlob.type });
    try {
      const result = await uploadFileForOrder(currentRecordingOrderId.current, 'video', file, setUploadProgress);
      if (result.success) {
        await refreshOrders();
        closeRecordingModal();
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, uploading: null }));
      setUploadProgress(0);
    }
  };

  const handleDownloadLetter = async (orderId) => {
    setLoadingStates(prev => ({ ...prev, downloading: orderId }));
    try {
      const result = await getLetterDownloadUrl(orderId);
      if (result.success && result.url) window.open(result.url, '_blank');
    } finally {
      setLoadingStates(prev => ({ ...prev, downloading: null }));
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      // Orders are now sourced from the user object in context,
      // so we just need to sort them.
      setOrders(user.orders ? [...user.orders].sort((a, b) => new Date(b.date) - new Date(a.date)) : []);
    }
  }, [isAuthenticated, user]); // Rerunning when user object updates is correct

  // Effect to handle setting the preview video source AFTER the state has updated
  useEffect(() => {
    if (videoRecordingState.recordedVideoBlob && previewVideoRef.current) {
      previewVideoRef.current.src = URL.createObjectURL(videoRecordingState.recordedVideoBlob);
    }
  }, [videoRecordingState.recordedVideoBlob]);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, loading, navigate]);

  const handleRequestCancellation = (orderId) => {
    setCancelConfirmation({ isOpen: true, orderId });
  };

  const handleConfirmCancellation = async () => {
    const { orderId } = cancelConfirmation;
    if (!orderId) return;

    setLoadingStates(prev => ({ ...prev, cancelling: orderId }));
    setCancelConfirmation({ isOpen: false, orderId: null }); // Close modal

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/orders/${orderId}/customer-cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const result = await response.json();
      if (result.success) toast.success(result.message);
      await refreshOrders();
    } finally {
      setLoadingStates(prev => ({ ...prev, cancelling: null }));
    }
  };

  if (loading || !user) return <AccountPageContainer><p>Loading...</p></AccountPageContainer>;

  return (
    <AccountPageContainer>
      <Title>My Account</Title>
      <EditButton onClick={() => navigate('/account/details')}>Edit Details</EditButton>
      <Detail><strong>Name:</strong> {user.name || 'Not provided'}</Detail>
      <Detail><strong>DOB:</strong> {user.dob ? new Date(user.dob).toLocaleDateString('en-GB') : 'N/A'}</Detail>
      <Detail><strong>Gender:</strong> {user.sex || 'Not provided'}</Detail>
      <Detail><strong>Email:</strong> {user.email}</Detail>
      <Address address={user.shipping_address} type="Shipping" />

      <OrderHistoryContainer>
        <SectionTitle>Order History</SectionTitle>
        {ordersLoading ? <p>Loading orders...</p> : orders.length > 0 ? (
          orders.map(order => {
            const isSubmitting = loadingStates.submitting === order.orderId;
            const isCancelling = loadingStates.cancelling === order.orderId;
            const isDownloading = loadingStates.downloading === order.orderId;

            // A rejected order should go back to the 'awaiting_submission' state for the tracker.
            const trackerStatus = order.status === 'rejected' ? 'awaiting_submission' : (order.status || 'awaiting_submission');

            const firstServiceDetails = allServices.find(s => order.items.some(item => s.id === item.id));
            const orderRequiresId = firstServiceDetails?.requiresId ?? true; // Default to true
            const orderRequiresVideo = firstServiceDetails?.requiresVideo ?? true; // Default to true
            const orderRequiresSupportingDocs = firstServiceDetails?.requiresSupportingDocs ?? false;
            const orderRequiresPhotoEvidence = firstServiceDetails?.requiresPhotoEvidence ?? false;

            const doesOrderRequireScr = order.items.some(item => {
              const service = allServices.find(s => s.id === item.id);
              return service?.requiresScr;
            });


            const needsDateRange = order.items.some(item => (item.name || item.n || '').toLowerCase().includes('sick note'));
            const datesValid = !needsDateRange || sickNoteDates[order.orderId]?.notApplicable || (sickNoteDates[order.orderId]?.start && sickNoteDates[order.orderId]?.end);
            const missingRequiredFile = (firstServiceDetails?.questionnaire || []).some(
              q => q.type === 'file' && q.required && !order.questionnaireFiles?.[q.name]?.filename
            );
            // Dynamically check if all *required* documents for this specific order are uploaded.
            const canSubmit = (!orderRequiresId || order.uploads?.id) &&
              (!orderRequiresVideo || order.uploads?.video) &&
              (!orderRequiresPhotoEvidence || order.uploads?.evidence) &&
              !missingRequiredFile &&
              datesValid;

            const isUnderReview = ['submitted', 'ai_draft_generated', 'ai_failed'].includes(order.status);

            return (
              <OrderCard key={order.orderId}>
                <OrderHeader>
                  <OrderInfo><strong>Date:</strong> {new Date(order.date).toLocaleDateString()}</OrderInfo>
                  <OrderInfo><strong>Delivery:</strong> {order.shippingOption === 'post' ? 'Post' : 'Email'}</OrderInfo>
                  <OrderInfo><strong>Total:</strong> £{order.total.toFixed(2)}</OrderInfo>
                  <OrderInfo><strong>ID:</strong> {order.orderId}</OrderInfo>
                </OrderHeader>
                <OrderItemsList>
                  {order.items.map(item => (
                    <OrderItem key={item.id}>
                      <div>
                        <ItemName>{item.name || item.n} (Qty: {item.quantity || item.q || 0})</ItemName>
                        {getProductDescription(item.name || item.n) && <ItemDescription>{getProductDescription(item.name || item.n)}</ItemDescription>}
                      </div>
                      <span>£{((item.price || item.p || 0) * (item.quantity || item.q || 0)).toFixed(2)}</span>
                    </OrderItem>
                  ))}
                </OrderItemsList>
                {order.status !== 'cancelled' && <OrderStatusTracker status={trackerStatus} />}
                {order.status === 'cancelled' ? (
                  <CancelledStatus><FaTimesCircle /> This order has been cancelled.</CancelledStatus>
                ) : order.status === 'completed' ? (
                  <CompletedSection>
                    <FaFileMedicalAlt /> Ready! Completed {new Date(order.completedAt).toLocaleDateString('en-GB')}.
                    <DownloadButton onClick={() => handleDownloadLetter(order.orderId)} disabled={isDownloading}>
                      {isDownloading ? <SpinningIcon /> : <FaDownload />} {isDownloading ? 'Preparing...' : 'Download Letter'}
                    </DownloadButton>
                  </CompletedSection>
                ) : isUnderReview ? (
                  <>
                    <SubmittedStatus><FaCheckCircle /> Documents submitted on {new Date(order.submittedAt).toLocaleDateString()}.</SubmittedStatus>
                    <CancelReviewButton onClick={() => handleCancelReview(order.orderId)} disabled={isCancelling}>
                      {isCancelling ? 'Cancelling...' : 'Cancel Review'}
                    </CancelReviewButton>
                  </>
                ) : (
                  <>
                    {order.status === 'rejected' && (
                      <RejectedStatus>
                        <p><strong><FaTimesCircle /> Your request has been rejected.</strong></p>
                        <p><strong>Reason:</strong> {order.rejectionReason}</p>
                        <p>Please correct any issues and re-submit your documents below. If you have any questions, please contact customer care.</p>
                      </RejectedStatus>
                    )}
                    <UploadSection>
                      <UploadList>
                        {firstServiceDetails?.requiresQuestionnaire && (
                          <UploadItem>
                            <UploadDetails>
                              <UploadLabel>Complete Questionnaire</UploadLabel>
                              <ItemDescription>Please complete the required questions for your service.</ItemDescription>
                            </UploadDetails>
                            {order.questionnaireAnswers ? (
                              <UploadActions>
                                <UploadStatus>
                                  <FaCheckCircle /> Completed
                                </UploadStatus>
                                <UploadButton as={Link} to={`/questionnaire/${order.items[0].id}`} style={{ textDecoration: 'none', backgroundColor: '#6c757d' }}>
                                  Edit Answers
                                </UploadButton>
                              </UploadActions>
                            ) : (
                              <UploadActions>
                                <UploadButton as={Link} to={`/questionnaire/${order.items[0].id}`} style={{ textDecoration: 'none' }}>
                                  <FaFileMedicalAlt /> Complete Form
                                </UploadButton>
                              </UploadActions>
                            )}
                          </UploadItem>
                        )}
                        {[
                          { type: 'id', text: 'Upload ID', description: 'Please upload a clear photo of your passport or driving licence.', accept: 'image/*,.pdf', required: orderRequiresId },
                          { type: 'evidence', text: 'Upload photo evidence', description: 'e.g., a positive test result, hospital letter, or prescription.', accept: 'image/*', required: orderRequiresPhotoEvidence },
                          { type: 'scr', text: 'Upload Summary Care Record (SCR)', description: 'Please upload a screenshot or PDF of your NHS Summary Care Record.', accept: 'image/*,.pdf', required: doesOrderRequireScr, optional: true, noDocsState: 'scr' },
                          { type: 'documents', text: 'Supporting documents', description: 'Upload any other relevant documents, like previous medical letters.', accept: 'image/*,.pdf,.doc,.docx', required: orderRequiresSupportingDocs, optional: true, noDocsState: 'documents' },
                          { type: 'video', text: 'Video confirmation', description: 'Record a short video stating your name, date of birth, and explaining your request.', accept: 'video/*', required: orderRequiresVideo },
                        ].filter(upload => upload.required).map((upload) => {
                          const isUploading = loadingStates.uploading?.orderId === order.orderId && loadingStates.uploading?.type === upload.type;
                          const uploadedFile = order.uploads?.[upload.type];
                          return (
                            <UploadItem key={upload.type}>
                              <UploadDetails>
                                <UploadLabel>{upload.text}</UploadLabel>
                                <ItemDescription>{upload.description}</ItemDescription>
                              </UploadDetails>
                              <UploadActions>
                                {isUploading ? (
                                  <UploadProgressWrapper>
                                    <UploadStatus style={{ color: '#005eb8' }}>
                                      <SpinningIcon /> {Math.round(uploadProgress)}%
                                    </UploadStatus>
                                    <ProgressBarContainer><ProgressBarFill width={uploadProgress} /></ProgressBarContainer>
                                  </UploadProgressWrapper>
                                ) : uploadedFile ? (
                                  <UploadStatus><FileInfo><FaCheckCircle /> {uploadedFile.filename}</FileInfo>
                                    <RemoveButton onClick={() => handleFileRemove(order.orderId, upload.type)}>Remove</RemoveButton>
                                  </UploadStatus>
                                ) : (
                                  <> {/* Only show "I don't have any" for truly optional uploads */}
                                    {upload.optional && (
                                      <NoDocsCheckboxContainer>
                                        <input type="checkbox" id={`no-${upload.type}-${order.orderId}`} checked={!!noDocuments[order.orderId]?.[upload.noDocsState]} onChange={(e) => handleNoDocumentsChange(order.orderId, upload.noDocsState, e.target.checked)} />
                                        <label htmlFor={`no-${upload.type}-${order.orderId}`}>I don't have any</label>
                                      </NoDocsCheckboxContainer>
                                    )}
                                    <UploadButton>
                                      <FaUpload /> Upload <input type="file" onChange={(e) => handleFileUpload(e, order.orderId, upload.type)} accept={upload.accept} />
                                    </UploadButton>
                                    {upload.type !== 'video' && upload.accept.includes('image') && (
                                      <UploadButton onClick={() => openPhotoModal(order.orderId, upload.type)}>
                                        <FaCamera /> Take Photo
                                      </UploadButton>
                                    )}
                                    {upload.type === 'video' && (
                                      <UploadButton onClick={() => openRecordingModal(order.orderId)}>
                                        <FaFileMedicalAlt /> Record Now
                                      </UploadButton>
                                    )}
                                  </>
                                )}
                              </UploadActions>
                            </UploadItem>
                          );
                        })}
                      </UploadList>
                      {needsDateRange && (
                        <SickNoteDatesContainer>
                          <p style={{ margin: '0', fontWeight: '700', fontSize: '1rem', color: '#333' }}>Letter Duration</p>
                          {!sickNoteDates[order.orderId]?.notApplicable && (
                            <DateInputGroup>
                              <div>
                                <label>From</label>
                                <input
                                  type="date"
                                  value={sickNoteDates[order.orderId]?.start || ''}
                                  onChange={(e) => handleSickNoteDateChange(order.orderId, 'start', e.target.value)}
                                />
                              </div>
                              <div>
                                <label>Until</label>
                                <input
                                  type="date"
                                  value={sickNoteDates[order.orderId]?.end || ''}
                                  onChange={(e) => handleSickNoteDateChange(order.orderId, 'end', e.target.value)}
                                />
                              </div>
                            </DateInputGroup>
                          )}
                          <NoDocsCheckboxContainer>
                            <input
                              type="checkbox"
                              id={`na-${order.orderId}`}
                              checked={!!sickNoteDates[order.orderId]?.notApplicable}
                              onChange={(e) => handleSickNoteDateChange(order.orderId, 'notApplicable', e.target.checked)}
                            />
                            <label htmlFor={`na-${order.orderId}`}>Not applicable / Other</label>
                          </NoDocsCheckboxContainer>
                        </SickNoteDatesContainer>
                      )}
                    </UploadSection>
                    <SubmitDocumentsButton onClick={() => handleSubmitDocuments(order.orderId)} disabled={!canSubmit || isSubmitting}>
                      {isSubmitting ? <SpinningIcon /> : 'Submit Documents for Review'}
                    </SubmitDocumentsButton>
                    <CancelOrderButton onClick={() => handleRequestCancellation(order.orderId)} disabled={isSubmitting || isCancelling}>
                      {isCancelling ? 'Cancelling...' : 'Cancel Order & Issue Refund'}
                    </CancelOrderButton>
                  </>
                )}
              </OrderCard>
            );
          })
        ) : <p>No past orders.</p>}
      </OrderHistoryContainer>

      {videoRecordingState.isRecordingModalOpen && (
        <RecordingModalOverlay>
          <RecordingModalContent>
            <CloseModalButton onClick={closeRecordingModal}><FaTimesCircle /></CloseModalButton>
            <h2>Record Your Video</h2>
            <VideoContainer>
              {videoRecordingState.recordedVideoBlob ? (
                <video ref={previewVideoRef} controls autoPlay loop />
              ) : (
                <>
                  <video ref={videoRef} autoPlay muted />
                  <InstructionsOverlay>
                    <p>State your full name and date of birth, and explain why you need the letter.</p>
                  </InstructionsOverlay>
                </>
              )}
            </VideoContainer>
            <RecordingControls>
              {!videoRecordingState.recordedVideoBlob && !videoRecordingState.isRecording && (
                <button className="start-button" onClick={startRecording} disabled={!videoRecordingState.isStreamReady}>
                  {videoRecordingState.isStreamReady ? 'Start Recording' : 'Waiting for camera...'}
                </button>
              )}
              {videoRecordingState.isRecording && <button className="stop-button" onClick={stopRecording}>Stop Recording</button>}
              {videoRecordingState.recordedVideoBlob && (
                <>
                  <button className="retake-button" onClick={retakeVideo}>Retake</button>
                  <button className="upload-button" onClick={handleUploadRecordedVideo}>Upload Video</button>
                </>
              )}
              <button className="cancel-button" onClick={closeRecordingModal}>Cancel</button>
            </RecordingControls>
          </RecordingModalContent>
        </RecordingModalOverlay>
      )}

      {photoCaptureState.isOpen && (
        <RecordingModalOverlay>
          <RecordingModalContent>
            <CloseModalButton onClick={closePhotoModal}><FaTimesCircle /></CloseModalButton>
            <h2>Take Photo</h2>
            <VideoContainer>
              {photoCaptureState.capturedBlob ? (
                <img src={URL.createObjectURL(photoCaptureState.capturedBlob)} alt="Captured" />
              ) : (
                <>
                  <video ref={videoRef} autoPlay muted playsInline />
                  <InstructionsOverlay>
                    <p>Align your {currentPhotoUploadType.current === 'id' ? 'ID' : 'document'} in the frame.</p>
                  </InstructionsOverlay>
                </>
              )}
            </VideoContainer>
            <RecordingControls>
              {!photoCaptureState.capturedBlob && (
                <button className="start-button" onClick={capturePhoto} disabled={!photoCaptureState.isStreamReady}>Capture Photo</button>
              )}
              {photoCaptureState.capturedBlob && (
                <>
                  <button className="retake-button" onClick={retakePhoto}>Retake</button>
                  <button className="upload-button" onClick={handleUploadCapturedPhoto}>Upload Photo</button>
                </>
              )}
              <button className="cancel-button" onClick={closePhotoModal}>Cancel</button>
            </RecordingControls>
          </RecordingModalContent>
        </RecordingModalOverlay>
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <ConfirmationModal
        isOpen={cancelConfirmation.isOpen}
        onClose={() => setCancelConfirmation({ isOpen: false, orderId: null })}
        onConfirm={handleConfirmCancellation}
        title="Confirm Order Cancellation"
        message="Are you sure you want to cancel your order? An 80% refund will be provided to cover administrative costs, please see T&C's for further information."
      />
      <LogoutButton onClick={() => { logout(); navigate('/'); }}>Logout</LogoutButton>
    </AccountPageContainer>
  );
};

export default Account;